import { Box, Button, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';

import TableSkeleton from '../../components/TableSkeleton';
import { useRoyaltyExpense } from '../../hooks/queries/useRoyaltyExpense';
import { downloadMonthlyRoyaltyExpense } from '../../services/api/Transfer';
import { downloadCSVFromText } from '../../services/utils';
import useSnackbar from '../../hooks/useSnackbar';

interface IProps {}

const useStyles = makeStyles((theme: Theme) => ({
  optionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  descriptionField: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 24,
  },

  bullet: {
    width: 3,
    height: 3,
    borderRadius: 50,
    marginTop: 9,
    marginRight: 4,
    backgroundColor: '#212121',
  },

  bulletedList: {
    display: 'flex',
    flexDirection: 'row',
  },

  headerRow: {
    '& *': {
      verticalAlign: 'middle',
    },
  },

  tableRow: {
    height: 52,
    '& a': {
      textDecoration: 'underline',
    },
  },

  tableCell: {
    textAlign: 'right',
    minWidth: '186px',
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:last-child': {
      paddingRight: 50,
    },
  },

  countValue: {
    fontWeight: 500,
  },
}));

const MonthlyRoyaltyExpenseList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const nowMoment = moment();
  const [selectedYear, setSelectedYear] = useState<number>(nowMoment.year());

  const { data, isLoading } = useRoyaltyExpense(selectedYear);

  // handlers
  const handleYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedYear(Number(event.target.value));
  };

  const handleDownloadClick = async () => {
    try {
      const { status, data } = await downloadMonthlyRoyaltyExpense(selectedYear);

      if (status === 200) {
        downloadCSVFromText(data, `RoyaltyExpense_${selectedYear}`);
      } else {
        throw new Error('Unable to download a CSV file.');
      }
    } catch (e) {
      snackbar.error(e.response ? e.response.data.message : e.message);
    }
  };

  const yearOptions = useMemo((): JSX.Element[] => {
    const startMoment = moment()
      .year(2019)
      .startOf('year');
    const options = [];

    while (nowMoment.isSameOrAfter(startMoment)) {
      const month = nowMoment.format('YYYY');
      options.push(
        <MenuItem key={`options-month-${month}`} value={nowMoment.format('YYYY')}>
          {nowMoment.format('YYYY')}
        </MenuItem>,
      );
      nowMoment.add(-1, 'year');
    }

    return options;
  }, []);

  return (
    <Box padding={1}>
      <Box className={classes.descriptionField}>
        <Typography style={{ fontSize: 18, marginBottom: 11 }}> Monthly Royalty Expense History</Typography>
        <Box className={classes.bulletedList}>
          <Box className={classes.bullet} />
          <Typography style={{ fontSize: 13 }}>Paid Coins(Exclude coins) = Credited coins + One-time coins + Subscription coins</Typography>
        </Box>
        <Box className={classes.bulletedList}>
          <Box className={classes.bullet} />
          <Typography style={{ fontSize: 13 }}>Ad Coins = Rewarded video coins + Offerwall coins</Typography>
        </Box>
      </Box>
      <Box marginBottom={'20px'} className={classes.optionContainer}>
        <Select style={{ width: 82, paddingLeft: 5 }} value={selectedYear} onChange={handleYearChange}>
          {yearOptions}
        </Select>
        <Button variant="outlined" size="small" color="primary" onClick={handleDownloadClick}>
          Download CSV
        </Button>
      </Box>
      <Paper>
        <Table>
          <TableHead className={classes.headerRow}>
            <TableRow>
              <TableCell style={{ textAlign: 'center' }} className={classes.tableCell}>
                Date
              </TableCell>
              <TableCell className={classes.tableCell}>Total royalty expense</TableCell>
              <TableCell className={classes.tableCell}>Paid Coins(Exclude Ads)</TableCell>
              <TableCell className={classes.tableCell}>Ad Coins</TableCell>
              <TableCell className={classes.tableCell}>Free Coins</TableCell>
            </TableRow>
          </TableHead>
          {isLoading && (
            <TableBody>
              <TableSkeleton rows={12} columns={5} />
            </TableBody>
          )}
          {!isLoading &&
            data &&
            data.map(item => (
              <TableRow hover className={classes.tableRow} key={`monthly-royalty-item-${item.year}-${item.month}`}>
                <TableCell style={{ textAlign: 'center' }} className={classes.tableCell}>
                  {moment()
                    .year(item.year)
                    .month(item.month - 1)
                    .format('MMMM YYYY')}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {Number(item.total).toLocaleString('en', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 3,
                  })}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {Number(item.paidExcludingAds).toLocaleString('en', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 3,
                  })}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {Number(item.ads).toLocaleString('en', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 3,
                  })}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {Number(item.free).toLocaleString('en', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 3,
                  })}
                </TableCell>
              </TableRow>
            ))}
        </Table>
      </Paper>
    </Box>
  );
};

export default MonthlyRoyaltyExpenseList;
